import { Graphics, Text } from 'pixi.js';
import { SegmentDivisionType } from '@/types/creator';
import ConfigurationRenderer from './ConfigurationRenderer';
import ConfigurationManager from './ConfigurationManager';
import RENDERER_CONFIG from '@/configs/rendererConfig';
import RendererUtils from './RendererUtils';
import Wing from './objects/Wing';

const { METRICS, COLORS, SEGMENT_DIVISION, ARMOR_RUNNERS, ARMOR } =
	RENDERER_CONFIG;

class ConfigurationDrawer {
	static readonly ARMOR_WIDTH = 10;

	private metrics = new Graphics({
		zIndex: METRICS.Z_INDEX,
	});
	private frame = new Graphics({
		zIndex: METRICS.Z_INDEX,
	});
	private segmentDivisions = new Graphics({
		zIndex: SEGMENT_DIVISION.Z_INDEX,
	});
	private runners = new Graphics({
		zIndex: ARMOR_RUNNERS.Z_INDEX,
	});
	private armor = new Graphics({
		zIndex: ARMOR.Z_INDEX,
	});
	private texts: Text[] = [];

	constructor(
		private renderer: ConfigurationRenderer,
		private manager: ConfigurationManager
	) {}

	initialize() {
		this.renderer.configuration.addChild(this.metrics);
		this.renderer.configuration.addChild(this.frame);
		this.renderer.configuration.addChild(this.segmentDivisions);
		this.renderer.configuration.addChild(this.runners);
		this.renderer.configuration.addChild(this.armor);
	}

	private calculatePositions() {
		const segments = this.manager.segments;

		const totalWidth = segments.reduce(
			(sum, segment) => sum + segment.scaledDimensions.width,
			0
		);

		const totalHeight = Math.max(
			...segments.map((segment) => segment.scaledDimensions.height)
		);

		let segmentXOffset = -totalWidth / 2;

		for (const segment of segments) {
			segment.container.position.set(segmentXOffset, -totalHeight / 2);

			segment.rollerBox.container.position.set(0, 0);
			segment.window.container.position.set(
				0,
				segment.rollerBox.scaledDimensions.height
			);

			let wingXoffset = 0;

			for (const wing of segment.window.wings) {
				wing.container.position.set(wingXoffset, 0);
				wingXoffset += wing.scaledDimensions.width;
			}

			segmentXOffset += segment.scaledDimensions.width;
		}
	}

	private drawConfigurationMetrics() {
		const totalScaledWidth = this.manager.segments.reduce(
				(sum, segment) => sum + segment.scaledDimensions.width,
				0
			),
			totalWidth = this.manager.segments.reduce(
				(sum, segment) => sum + segment.dimensions.width,
				0
			),
			totalScaledHeight = Math.max(
				...this.manager.segments.map(
					(segment) => segment.scaledDimensions.height
				)
			),
			totalHeight = Math.max(
				...this.manager.segments.map((segment) => segment.dimensions.height)
			);

		// total width

		this.metrics
			.moveTo(-totalScaledWidth / 2, -totalScaledHeight / 2)
			.lineTo(
				-totalScaledWidth / 2,
				-totalScaledHeight / 2 - METRICS.EDGES_LENGTH
			)
			.moveTo(totalScaledWidth / 2, -totalScaledHeight / 2)
			.lineTo(
				totalScaledWidth / 2,
				-totalScaledHeight / 2 - METRICS.EDGES_LENGTH
			)
			.stroke({ color: COLORS.BLACK, pixelLine: true });

		RendererUtils.drawMetricLine(
			{
				x: -totalScaledWidth / 2,
				y: -totalScaledHeight / 2 - METRICS.ARROW_LINE_OFFSET,
			},
			{
				x: totalScaledWidth / 2,
				y: -totalScaledHeight / 2 - METRICS.ARROW_LINE_OFFSET,
			},
			this.metrics
		);

		const widthText = new Text({
			text: totalWidth,
			style: METRICS.TEXT_STYLE.clone(),
			resolution: METRICS.TEXT_RESOLUTION,
		});

		this.renderer.configuration.addChild(widthText);
		this.texts.push(widthText);

		widthText.anchor.set(0.5);
		widthText.position.set(
			0,
			-totalScaledHeight / 2 -
				METRICS.ARROW_LINE_OFFSET -
				METRICS.TEXT_TO_LINE_OFFSET
		);

		// total height

		this.metrics
			.moveTo(totalScaledWidth / 2, -totalScaledHeight / 2)
			.lineTo(
				totalScaledWidth / 2 + METRICS.EDGES_LENGTH,
				-totalScaledHeight / 2
			)
			.moveTo(totalScaledWidth / 2, totalScaledHeight / 2)
			.lineTo(
				totalScaledWidth / 2 + METRICS.EDGES_LENGTH,
				totalScaledHeight / 2
			)
			.stroke({ color: COLORS.BLACK, pixelLine: true });

		RendererUtils.drawMetricLine(
			{
				x: totalScaledWidth / 2 + METRICS.ARROW_LINE_OFFSET,
				y: -totalScaledHeight / 2,
			},
			{
				x: totalScaledWidth / 2 + METRICS.ARROW_LINE_OFFSET,
				y: totalScaledHeight / 2,
			},
			this.metrics,
			true
		);

		const totalHeightText = new Text({
			text: totalHeight,
			style: METRICS.TEXT_STYLE.clone(),
			resolution: METRICS.TEXT_RESOLUTION,
		});

		this.renderer.configuration.addChild(totalHeightText);
		this.texts.push(totalHeightText);

		totalHeightText.anchor.set(0.5);
		totalHeightText.position.set(
			totalScaledWidth / 2 + METRICS.ARROW_LINE_OFFSET / 1.5,
			0
		);
		totalHeightText.rotation = Math.PI * 1.5;

		// segments

		const highestSegmentHeight = this.manager.segments.reduce(
			(prev, curr) => Math.max(prev, curr.scaledDimensions.height),
			0
		);

		this.manager.segments.forEach((segment, idx, arr) => {
			const isLast = idx === arr.length - 1,
				prevSegment = arr[idx - 1],
				higherSegment = [segment, prevSegment].sort(
					(a, b) => b.dimensions.height - a.dimensions.height
				)[0];
			const { width } = segment.scaledDimensions;
			const { width: originalWidth } = segment.dimensions;

			const startY = prevSegment
					? higherSegment.container.y + higherSegment.container.height
					: segment.container.y + segment.container.height,
				endY = segment.container.y + highestSegmentHeight,
				startX = segment.container.x;

			this.metrics
				.moveTo(startX, startY)
				.lineTo(startX, endY + METRICS.EDGES_LENGTH)
				.stroke({ color: COLORS.LIGHT_GRAY, pixelLine: true });

			if (isLast)
				this.metrics
					.moveTo(startX + width, endY)
					.lineTo(startX + width, endY + METRICS.EDGES_LENGTH)
					.stroke({ color: COLORS.LIGHT_GRAY, pixelLine: true });

			RendererUtils.drawMetricLine(
				{
					x: startX,
					y: endY + METRICS.ARROW_LINE_OFFSET,
				},
				{
					x: startX + width,
					y: endY + METRICS.ARROW_LINE_OFFSET,
				},
				this.metrics,
				false
			);

			const segmentWidthXHeightText = new Text({
				text: `${originalWidth} x ${segment.window.dimensions.height}`,
				style: METRICS.TEXT_STYLE.clone(),
				resolution: METRICS.TEXT_RESOLUTION,
			});

			this.renderer.configuration.addChild(segmentWidthXHeightText);
			this.texts.push(segmentWidthXHeightText);

			segmentWidthXHeightText.anchor.set(0.5);
			segmentWidthXHeightText.position.set(
				startX + width / 2,
				endY + METRICS.ARROW_LINE_OFFSET / 2
			);
		});

		// roller box

		const firstSegment = this.manager.segments[0],
			firstRollerBox = firstSegment.rollerBox;

		this.metrics
			.moveTo(firstSegment.container.x, firstSegment.container.y)
			.lineTo(
				firstSegment.container.x - METRICS.EDGES_LENGTH,
				firstSegment.container.y
			)
			.moveTo(
				firstSegment.container.x,
				firstSegment.container.y + firstRollerBox.scaledDimensions.height
			)
			.lineTo(
				firstSegment.container.x - METRICS.EDGES_LENGTH,
				firstSegment.container.y + firstRollerBox.scaledDimensions.height
			)
			.stroke({ color: COLORS.BLACK, pixelLine: true });

		RendererUtils.drawMetricLine(
			{
				x: firstSegment.container.x - METRICS.ARROW_LINE_OFFSET,
				y: firstSegment.container.y,
			},
			{
				x: firstSegment.container.x - METRICS.ARROW_LINE_OFFSET,
				y: firstSegment.container.y + firstRollerBox.scaledDimensions.height,
			},
			this.metrics,
			true
		);

		const rollerBoxHeightText = new Text({
			text: firstRollerBox.dimensions.height,
			style: METRICS.TEXT_STYLE.clone(),
			resolution: METRICS.TEXT_RESOLUTION,
		});

		this.renderer.configuration.addChild(rollerBoxHeightText);
		this.texts.push(rollerBoxHeightText);

		rollerBoxHeightText.anchor.set(0.5);
		rollerBoxHeightText.position.set(
			firstSegment.container.x - METRICS.ARROW_LINE_OFFSET * 1.5,
			firstSegment.container.y + firstRollerBox.scaledDimensions.height / 2
		);
		rollerBoxHeightText.rotation = Math.PI * 1.5;
	}

	private drawSegmentDivisions() {
		const divisionWidth = Wing.DIVISION_WIDTH;

		for (let index = 0; index < this.manager.segments.length; index++) {
			const segment = this.manager.segments[index],
				nextSegment = this.manager.segments[index + 1];
			const startX = segment.container.x;
			const { width } = segment.scaledDimensions;
			const { height } = segment.window.scaledDimensions;

			const jointX = startX + width,
				startY =
					segment.container.y + segment.rollerBox.scaledDimensions.height;

			const endY = startY + height;

			switch (segment.nextJoint?.divisionType) {
				case SegmentDivisionType.NONE:
					break;
				case SegmentDivisionType.THIN:
					this.segmentDivisions
						.rect(jointX - divisionWidth / 2, startY, divisionWidth, height)
						.fill({ color: COLORS.WHITE })
						.stroke({ color: COLORS.BLACK, pixelLine: true });

					RendererUtils.drawDash(
						this.segmentDivisions,
						jointX,
						startY,
						jointX,
						endY,
						10,
						5
					);
					this.segmentDivisions.stroke({
						color: COLORS.BLACK,
						pixelLine: true,
					});

					break;
				case SegmentDivisionType.WIDE:
					this.segmentDivisions
						.rect(jointX - divisionWidth, startY, divisionWidth, height)
						.fill({ color: COLORS.WHITE })
						.stroke({ color: COLORS.BLACK, pixelLine: true });
					this.segmentDivisions
						.rect(
							jointX,
							startY,
							divisionWidth,
							nextSegment.window.scaledDimensions.height
						)
						.fill({ color: COLORS.WHITE })
						.stroke({ color: COLORS.BLACK, pixelLine: true });

					this.segmentDivisions.moveTo(jointX, startY);
					this.segmentDivisions.lineTo(jointX, endY);
					this.segmentDivisions.stroke({
						color: COLORS.BLACK,
						pixelLine: true,
					});
					break;
			}
		}
	}

	private drawRunners() {
		if (!this.manager.withArmor) return;

		const firstSegment = this.manager.segments[0],
			lastSegment = this.manager.segments.at(-1)!;

		const startY = firstSegment.container.y + firstSegment.window.container.y;

		this.runners
			.rect(
				firstSegment.container.x,
				startY,
				ConfigurationDrawer.ARMOR_WIDTH,
				firstSegment.window.scaledDimensions.height
			)
			.fill({ color: COLORS.WHITE })
			.stroke({ color: COLORS.BLACK, pixelLine: true });

		this.runners
			.rect(
				lastSegment.container.x +
					lastSegment.scaledDimensions.width -
					ConfigurationDrawer.ARMOR_WIDTH,
				startY,
				ConfigurationDrawer.ARMOR_WIDTH,
				lastSegment.window.scaledDimensions.height
			)
			.fill({ color: COLORS.WHITE })
			.stroke({ color: COLORS.BLACK, pixelLine: true });
	}

	private drawArmor() {
		if (!this.manager.withArmor) return;
		if (!this.manager.segments.length) return;

		const shellHeight = ARMOR.SHELL_HEIGHT * this.renderer.scale;

		for (const sequence of this.manager.segmentSequences) {
			if (!sequence.length) continue;

			const firstSegment = sequence[0],
				lastSegment = sequence.at(-1)!;
			const lowestSegmentHeight = Math.min(
				...sequence.map((segment) => segment.window.dimensions.height)
			);
			const shellsQuantity = Math.floor(
				Math.min(
					ARMOR.MAX_SHELLS_QUANTITY,
					lowestSegmentHeight / 2 / ARMOR.SHELL_HEIGHT
				)
			);
			if (!shellsQuantity) continue;

			const armorStartX = firstSegment.container.x;
			const armorWidth =
				lastSegment.container.x +
				lastSegment.scaledDimensions.width -
				armorStartX;
			const armorStartY =
				firstSegment.container.y +
				firstSegment.rollerBox.scaledDimensions.height;
			const armorHeight = shellsQuantity * shellHeight;
			const lineColor = sequence.some((segment) => segment.isArmorError)
				? COLORS.RED
				: COLORS.BLACK;

			this.armor
				.rect(armorStartX, armorStartY, armorWidth, armorHeight)
				.fill({ color: COLORS.WHITE });

			for (let index = 0; index < shellsQuantity; index++) {
				if (!index)
					this.armor
						.moveTo(armorStartX, armorStartY)
						.lineTo(armorStartX + armorWidth, armorStartY)
						.stroke({ color: lineColor, pixelLine: true });

				const shellBottomY = armorStartY + (index + 1) * shellHeight;

				this.armor
					.moveTo(armorStartX, shellBottomY)
					.lineTo(armorStartX + armorWidth, shellBottomY)
					.stroke({ color: lineColor, pixelLine: true });
			}
		}
	}

	build() {
		this.calculatePositions();

		this.clear();

		for (const segment of this.manager.segments) {
			segment.draw();
		}

		this.drawConfigurationMetrics();
		this.drawSegmentDivisions();
		this.drawRunners();
		this.drawArmor();
	}

	clear() {
		this.metrics.clear();
		this.frame.clear();
		this.segmentDivisions.clear();
		this.runners.clear();
		this.armor.clear();

		for (const text of this.texts) {
			text.removeFromParent();
			text.destroy();
		}

		this.texts = [];
	}
}

export default ConfigurationDrawer;
